/**
 * ADMIN UPDATES PAGE
 * 
 * Admin-only page for creating, editing, and deleting updates.
 * Accessible at /admin/updates
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  collection, 
  query, 
  orderBy, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  doc,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { 
  Megaphone, 
  Sparkles, 
  Bug, 
  Wrench, 
  AlertTriangle,
  Plus,
  Pencil,
  Trash2,
  Pin,
  PinOff,
  Loader2,
  ArrowLeft,
  X,
  Eye
} from 'lucide-react';

// Update categories
const CATEGORIES = {
  announcement: {
    label: 'Announcement',
    icon: Megaphone,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
  },
  feature: {
    label: 'New Feature',
    icon: Sparkles,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/20',
  },
  bugfix: {
    label: 'Bug Fix',
    icon: Bug,
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
  },
  improvement: {
    label: 'Improvement',
    icon: Wrench,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/20',
  },
  maintenance: {
    label: 'Maintenance',
    icon: AlertTriangle,
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
  },
};

type CategoryId = keyof typeof CATEGORIES;

interface Update {
  id: string;
  title: string;
  content: string;
  category: CategoryId;
  createdAt: Date;
  isPinned: boolean;
}

export default function AdminUpdates() {
  const [updates, setUpdates] = useState<Update[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState<Update | null>(null);
  
  // Form state
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<CategoryId>('announcement');
  const [isPinned, setIsPinned] = useState(false);
  const [notify, setNotify] = useState(false);
  const [notifyPush, setNotifyPush] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Check admin status
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Check if user is admin (adjust this based on your admin check logic)
        const userDoc = await getDocs(query(collection(db, 'users')));
        const userData = userDoc.docs.find(d => d.id === user.uid)?.data();
        setIsAdmin(userData?.isAdmin === true);
      } else {
        setIsAdmin(false);
      }
      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, []);

  // Load updates
  useEffect(() => {
    const loadUpdates = async () => {
      try {
        const updatesRef = collection(db, 'updates');
        const q = query(updatesRef, orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        
        const loadedUpdates: Update[] = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.title,
            content: data.content,
            category: data.category,
            createdAt: data.createdAt instanceof Timestamp 
              ? data.createdAt.toDate() 
              : new Date(data.createdAt),
            isPinned: data.isPinned || false,
          };
        });
        
        setUpdates(loadedUpdates);
      } catch (error) {
        console.error('Error loading updates:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isAdmin) {
      loadUpdates();
    }
  }, [isAdmin]);

  // Open modal for new update
  const handleNewUpdate = () => {
    setEditingUpdate(null);
    setTitle('');
    setContent('');
    setCategory('announcement');
    setIsPinned(false);
    setNotify(false);
    setNotifyPush(false);
    setShowModal(true);
  };

  // Open modal for editing
  const handleEdit = (update: Update) => {
    setEditingUpdate(update);
    setTitle(update.title);
    setContent(update.content);
    setCategory(update.category);
    setIsPinned(update.isPinned);
    // Notifications only fire on create, so default these off when editing.
    setNotify(false);
    setNotifyPush(false);
    setShowModal(true);
  };

  // Save update (create or edit)
  const handleSave = async () => {
    if (!title.trim() || !content.trim()) return;
    
    setIsSaving(true);
    try {
      if (editingUpdate) {
        // Update existing
        await updateDoc(doc(db, 'updates', editingUpdate.id), {
          title: title.trim(),
          content: content.trim(),
          category,
          isPinned,
          updatedAt: serverTimestamp(),
        });
        
        setUpdates(prev => prev.map(u => 
          u.id === editingUpdate.id 
            ? { ...u, title: title.trim(), content: content.trim(), category, isPinned }
            : u
        ));
      } else {
        // Create new
        const docRef = await addDoc(collection(db, 'updates'), {
          title: title.trim(),
          content: content.trim(),
          category,
          isPinned,
          // The onUpdatePublished function reads these to notify users (in-app
          // always when `notify`, plus push when `notifyPush`).
          notify,
          notifyPush: notify && notifyPush,
          createdAt: serverTimestamp(),
        });
        
        setUpdates(prev => [{
          id: docRef.id,
          title: title.trim(),
          content: content.trim(),
          category,
          isPinned,
          createdAt: new Date(),
        }, ...prev]);
      }
      
      setShowModal(false);
    } catch (error) {
      console.error('Error saving update:', error);
      alert('Failed to save update. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Toggle pin status
  const handleTogglePin = async (update: Update) => {
    try {
      await updateDoc(doc(db, 'updates', update.id), {
        isPinned: !update.isPinned,
      });
      
      setUpdates(prev => prev.map(u => 
        u.id === update.id ? { ...u, isPinned: !u.isPinned } : u
      ));
    } catch (error) {
      console.error('Error toggling pin:', error);
    }
  };

  // Delete update
  const handleDelete = async (updateId: string) => {
    try {
      await deleteDoc(doc(db, 'updates', updateId));
      setUpdates(prev => prev.filter(u => u.id !== updateId));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting update:', error);
      alert('Failed to delete update. Please try again.');
    }
  };

  // Format date
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Auth check
  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-pink-400 animate-spin" />
      </div>
    );
  }

  // Not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card p-8 text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-glass mb-2 font-heading">Access Denied</h1>
          <p className="text-glass-secondary font-body mb-4">
            You don't have permission to access this page.
          </p>
          <Link to="/" className="btn-primary inline-block">
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link 
              to="/admin" 
              className="inline-flex items-center gap-2 text-glass-muted hover:text-glass mb-2 font-body"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Admin
            </Link>
            <h1 className="text-2xl font-bold text-glass font-heading">Manage Updates</h1>
          </div>
          
          <div className="flex items-center gap-3">
            <Link
              to="/updates"
              className="flex items-center gap-2 px-4 py-2 bg-slate-700/50 text-glass-secondary rounded-lg hover:bg-slate-600/50 transition-default font-body"
            >
              <Eye className="w-4 h-4" />
              View Public Page
            </Link>
            <button
              onClick={handleNewUpdate}
              className="flex items-center gap-2 btn-primary"
            >
              <Plus className="w-4 h-4" />
              New Update
            </button>
          </div>
        </div>

        {/* Updates List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-pink-400 animate-spin" />
          </div>
        ) : updates.length === 0 ? (
          <div className="glass-card p-8 text-center">
            <Megaphone className="w-12 h-12 text-glass-muted mx-auto mb-3" />
            <p className="text-glass-secondary font-body mb-4">
              No updates yet. Create your first one!
            </p>
            <button onClick={handleNewUpdate} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Create Update
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {updates.map((update) => {
              const cat = CATEGORIES[update.category];
              const Icon = cat.icon;
              
              return (
                <div key={update.id} className="glass-card p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg ${cat.bgColor} flex-shrink-0`}>
                        <Icon className={`w-4 h-4 ${cat.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-glass font-body">{update.title}</h3>
                          {update.isPinned && (
                            <span className="text-xs bg-pink-500/20 text-pink-400 px-2 py-0.5 rounded-full font-body">
                              Pinned
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-glass-muted font-body">
                          {cat.label} • {formatDate(update.createdAt)}
                        </p>
                        <p className="text-glass-secondary font-body mt-1 line-clamp-2">
                          {update.content}
                        </p>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleTogglePin(update)}
                        className={`p-2 rounded-lg transition-default ${
                          update.isPinned 
                            ? 'text-pink-400 hover:bg-pink-500/20' 
                            : 'text-glass-muted hover:bg-slate-700/50'
                        }`}
                        title={update.isPinned ? 'Unpin' : 'Pin'}
                      >
                        {update.isPinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleEdit(update)}
                        className="p-2 text-glass-muted hover:text-glass hover:bg-slate-700/50 rounded-lg transition-default"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {deleteConfirm === update.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(update.id)}
                            className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-default"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="px-2 py-1 text-xs bg-slate-600 text-glass rounded hover:bg-slate-500 transition-default"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(update.id)}
                          className="p-2 text-glass-muted hover:text-red-400 hover:bg-red-500/20 rounded-lg transition-default"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass-modal max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-glass font-heading">
                {editingUpdate ? 'Edit Update' : 'New Update'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-glass-muted hover:text-glass hover:bg-slate-700/50 rounded-lg transition-default"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Category */}
              <div>
                <label className="text-sm font-medium text-glass-secondary block mb-2 font-body">
                  Category
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {Object.entries(CATEGORIES).map(([key, cat]) => {
                    const Icon = cat.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => setCategory(key as CategoryId)}
                        className={`flex items-center gap-2 p-2 rounded-lg border transition-default font-body text-sm ${
                          category === key
                            ? `${cat.bgColor} border-current ${cat.color}`
                            : 'border-slate-600 text-glass-secondary hover:border-slate-500'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {cat.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="text-sm font-medium text-glass-secondary block mb-1 font-body">
                  Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 bg-slate-800/50 text-glass font-body"
                  placeholder="What's new?"
                />
              </div>

              {/* Content */}
              <div>
                <label className="text-sm font-medium text-glass-secondary block mb-1 font-body">
                  Content
                </label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={5}
                  className="w-full px-3 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 bg-slate-800/50 text-glass font-body resize-none"
                  placeholder="Describe the update..."
                />
              </div>

              {/* Pin toggle */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPinned}
                  onChange={(e) => setIsPinned(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 text-pink-500 focus:ring-pink-500"
                />
                <span className="text-glass-secondary font-body">
                  Pin this update (appears at top)
                </span>
              </label>

              {/* Notify users — only meaningful on a NEW update (notifications
                  fire once, on publish; editing won't re-notify). */}
              {!editingUpdate && (
                <div className="rounded-lg border border-slate-600 p-3 space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notify}
                      onChange={(e) => setNotify(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-600 text-pink-500 focus:ring-pink-500"
                    />
                    <span className="text-glass-secondary font-body">
                      Notify all users (in-app notification)
                    </span>
                  </label>
                  <label
                    className={`flex items-center gap-3 cursor-pointer ${
                      notify ? '' : 'opacity-50 pointer-events-none'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={notify && notifyPush}
                      disabled={!notify}
                      onChange={(e) => setNotifyPush(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-600 text-pink-500 focus:ring-pink-500"
                    />
                    <span className="text-glass-secondary font-body">
                      Also send a push notification
                    </span>
                  </label>
                  <p className="text-xs text-glass-muted font-body">
                    Notifications send once, when you publish. Editing later won't re-notify.
                  </p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 bg-slate-700/50 text-glass-secondary rounded-lg hover:bg-slate-600/50 transition-default font-body font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || !title.trim() || !content.trim()}
                className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  editingUpdate ? 'Save Changes' : 'Publish Update'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
